import dayjs from 'dayjs';
import { t } from 'i18next';
import _ from 'lodash';

import { EStatus } from '@common/types';
import { DEFAULT_DATE_FORMAT, DEFAULT_DATE_TIME_FORMAT } from '@common/utils';

import type { FormInstance } from 'antd';

export const requiredField = (field: string, required = true) => ({
  required,
  message: t('form.required', { field }),
});

export const mergeNames = (prefix: string[] | null, ...names: string[]) => {
  if (!prefix?.length) return names;
  return [...prefix, ...names];
};

export const placeholderField = (
  field: string,
  fieldType: 'input' | 'select' = 'input',
) =>
  fieldType === 'input'
    ? t('form.inputPlaceholder', { field })
    : t('form.selectPlaceholder', { field });

export const isReadOnlyField = (field: string, readOnlyFields: string[]) => {
  let isField = false;
  if (readOnlyFields?.length) {
    isField = readOnlyFields.includes(field);
  }
  return isField;
};

/**
 * Get the index of a form item in the entityForm array.
 * @param {{entityForm, key, ns}} args - The args object.
 * @param {Record<string, any>[]} args.entityForm - The entityForm array.
 * @param {string} args.key - The key of the form item.
 * @param {string} [args.ns] - The namespace of the form item.
 * @returns {number} The index of the form item, or -1 if not found.
 */
export function getEntityFormIdx(args: {
  entityForm: Record<string, any>[];
  key: string;
  ns?: string;
}): number {
  const { entityForm, key, ns } = args;

  let idx = -1,
    _key = key;
  if (ns?.length) {
    _key = `${ns}/${key}`;
  }
  entityForm.forEach((form, index) => {
    if (form.name === _key) {
      idx = index;
    }
  });

  return idx;
}

const transformFieldValue = (
  value: any,
  key: string,
  dateFields: string[],
  dateTimeFields: string[],
): any => {
  const isDate = dateFields.find((d) => d === key);
  const isDateTime = dateTimeFields.find((d) => d === key);

  if (isDate) {
    return dayjs(dayjs(value as Date).format(DEFAULT_DATE_FORMAT));
  }

  if (isDateTime) {
    return dayjs(dayjs(value as Date).format(DEFAULT_DATE_TIME_FORMAT));
  }

  return value;
};

export const toEntityForm = <T extends Record<string, any>>(props: {
  ns?: string[];
  entityForm?: Record<string, any>[];
  formObj?: T;
  dateFields?: string[];
  dateTimeFields?: string[];
}) => {
  const {
    ns = [],
    entityForm = [],
    formObj = {},
    dateFields = [],
    dateTimeFields = [],
  } = props;

  const _entityForm = [...entityForm];
  const toDelete: number[] = [];

  const keys = Object.keys(formObj);

  for (const element of keys) {
    const key = element;
    const idx = getEntityFormIdx({ entityForm, key });

    const value = transformFieldValue(
      (formObj as T)[key],
      key,
      dateFields,
      dateTimeFields,
    );

    const formItem = { name: mergeNames(ns, key), value };

    // Overwrite existing values
    if (idx > -1) {
      toDelete.push(idx);
    }

    _entityForm.push(formItem);
  }

  return [..._entityForm.filter((_form, idx) => toDelete.indexOf(idx) === -1)];
};

export const setNestedDynamicFields = (
  formRef: FormInstance<any>,
  names: string | string[],
  value: any,
) => {
  const obj: Record<string, unknown> = {};
  _.set(obj, names, value);
  formRef.setFieldsValue(obj);
};

/**
 * This function takes an object and a nested key and returns the value
 * associated with the key. If the key is an array, the function will
 * traverse the object using the array as a path. If the key is a string, the
 * function will treat it as a simple key lookup.
 * @param {object} data The object to search.
 * @param {string|string[]} nested The key or array of keys to search for.
 * @returns {any} The value found in the object, or undefined.
 */
export function findFieldValue(data: object, nested: string | string[]): any {
  return Array.isArray(nested)
    ? _.get(data, nested.join('.'))
    : _.get(data, nested);
}

export const isFormRefConnected = (formRef: any) =>
  formRef?.getFieldInstance !== undefined;

export const isNewMode = (mode?: string) => mode === 'new';
export const isEditMode = (mode?: string) => !isNewMode(mode);

export const isDeactivated = (status: EStatus) =>
  status === EStatus.DEACTIVATED;
